import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import ProductShowcase from './ProductShowcase';
import ProductDocsSection from './ProductDocsSection';

const API_BASE_URL = 'https://www.lankastudio.cn/api';

function getImageUrl(img) {
  if (!img) return null;
  if (typeof img === 'string') return img;
  if (img.url) return img.url;
  if (img.id) return `${API_BASE_URL}/images/${img.id}`;
  return null;
}

function ProductDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [product, setProduct] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const touchStartRef = useRef({ x: 0, y: 0 });
  const scrollContainerRef = useRef(null);

  useEffect(() => {
    if (!id) return;
    let cancelled = false;
    async function fetchProduct() {
      try {
        setLoading(true);
        const res = await fetch(`${API_BASE_URL}/products/${id}`);
        if (!res.ok) throw new Error('获取失败');
        const data = await res.json();
        if (!cancelled) setProduct(data);
      } catch (err) {
        if (!cancelled) setError(err.message);
      } finally {
        if (!cancelled) setLoading(false);
      }
    }
    fetchProduct();
    return () => { cancelled = true; };
  }, [id]);

  const handleTouchStart = (e) => {
    touchStartRef.current = { x: e.touches[0].clientX, y: e.touches[0].clientY };
  };

  const handleTouchEnd = (e) => {
    const { clientX: endX, clientY: endY } = e.changedTouches[0];
    const { x: startX, y: startY } = touchStartRef.current;
    const deltaX = endX - startX;
    const deltaY = endY - startY;
    // 向左滑动返回：水平位移足够且以水平为主
    if (deltaX < -60 && Math.abs(deltaX) > Math.abs(deltaY) * 1.2) {
      navigate(-1);
    }
  };

  const goBack = () => navigate(-1);

  if (loading) {
    return (
      <div style={styles.container}>
        <ProductShowcase />
        <div style={styles.overlay} />
        <div style={styles.contentLayer}>
          <div style={styles.loading}>
            <div style={styles.spinner} />
            <p>加载中...</p>
          </div>
        </div>
      </div>
    );
  }

  if (error || !product) {
    return (
      <div style={styles.container}>
        <ProductShowcase />
        <div style={styles.overlay} />
        <div style={styles.contentLayer}>
          <button onClick={goBack} style={styles.backButton}>← 返回</button>
          <div style={styles.error}>
            <p>❌ {error || '产品不存在'}</p>
          </div>
        </div>
      </div>
    );
  }

  const images = product.images || [];
  const rawSpecs = product.specifications || [];
  const specs = [...rawSpecs].sort((a, b) => {
    const getScale = (s) => {
      const m = String(s.model_scale || '').match(/:(\d+)|(\d+)$|\/(\d+)/);
      return parseInt(m ? (m[1] || m[2] || m[3]) : '0', 10) || 999;
    };
    return getScale(b) - getScale(a);
  });
  const purchaseLinks = product.purchase_links || [];
  const descCn = product.description_cn || product.product_description_cn;
  const descEn = product.description_en || product.product_description_en;
  const currentImageUrl = getImageUrl(images[currentImageIndex]);

  return (
    <div
      style={styles.container}
      onTouchStart={handleTouchStart}
      onTouchEnd={handleTouchEnd}
    >
      <ProductShowcase />
      <div style={styles.overlay} />
      <div style={styles.contentLayer} ref={scrollContainerRef}>
        <button onClick={goBack} style={styles.backButton}>← 返回</button>

        <div style={styles.pageContent}>
          {/* 图片轮播 */}
          <div style={styles.imageSection}>
            {images.length > 0 && currentImageUrl ? (
              <>
                <img src={currentImageUrl} alt={product.product_name_cn} style={styles.mainImage} />
                {images.length > 1 && (
                  <>
                    <button
                      onClick={() => setCurrentImageIndex((i) => (i - 1 + images.length) % images.length)}
                      style={styles.carouselBtn}
                    >
                      ‹
                    </button>
                    <button
                      onClick={() => setCurrentImageIndex((i) => (i + 1) % images.length)}
                      style={{ ...styles.carouselBtn, right: 10 }}
                    >
                      ›
                    </button>
                    <div style={styles.imageIndicators}>
                      {images.map((_, idx) => (
                        <span
                          key={idx}
                          style={{
                            ...styles.indicator,
                            backgroundColor: idx === currentImageIndex ? '#fff' : 'rgba(255,255,255,0.4)',
                          }}
                          onClick={() => setCurrentImageIndex(idx)}
                        />
                      ))}
                    </div>
                  </>
                )}
              </>
            ) : (
              <div style={styles.noImage}>暂无图片</div>
            )}
          </div>

          {/* 产品信息 */}
          <div style={styles.infoSection}>
            <h1 style={styles.title}>{product.product_name_cn}</h1>
            {product.product_name_en && <p style={styles.subtitle}>{product.product_name_en}</p>}

            <div style={styles.specsSection}>
              <h4 style={styles.sectionTitle}>规格与售价</h4>
              {specs.length > 0 ? (
                <div style={styles.tableWrap}>
                  <table style={styles.table}>
                    <thead>
                      <tr>
                        <th style={styles.th}>比例</th>
                        <th style={styles.th}>尺寸(cm)</th>
                        <th style={styles.th}>白模</th>
                        <th style={styles.th}>涂装</th>
                        {specs.some(s => s.remark) && <th style={styles.th}>备注</th>}
                      </tr>
                    </thead>
                    <tbody>
                      {specs.map((s) => (
                        <tr key={s.id}>
                          <td style={styles.td}>{s.model_scale}</td>
                          <td style={styles.td}>{s.size_x}×{s.size_y}×{s.size_z}</td>
                          <td style={styles.td}>{s.white_model_price != null ? `¥${Number(s.white_model_price).toFixed(2)}` : '-'}</td>
                          <td style={styles.td}>{s.painted_price != null ? `¥${Number(s.painted_price).toFixed(2)}` : '-'}</td>
                          {specs.some(sp => sp.remark) && <td style={styles.td}>{s.remark || '-'}</td>}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ) : product.price ? (
                <p style={styles.emptyText}>¥{product.price}</p>
              ) : (
                <p style={styles.emptyText}>规格与售价信息待补充</p>
              )}
            </div>

            <p style={styles.desc}>{descCn || '暂无描述'}</p>
            {descEn && <p style={styles.descEn}>{descEn}</p>}

            <ProductDocsSection product={product} />

            <div style={styles.tags}>
              {product.category && <span style={styles.tag}>{product.category}</span>}
              {product.status && <span style={styles.tag}>{product.status}</span>}
            </div>

            <div style={styles.purchaseSection}>
              <h4 style={styles.sectionTitle}>购买渠道</h4>
              {purchaseLinks.length > 0 ? (
                <div style={styles.purchaseGrid}>
                  {purchaseLinks.map((link, idx) => (
                    <a key={idx} href={link.url} target="_blank" rel="noopener noreferrer" style={styles.purchaseBtn}>
                      {link.icon} {link.channel}
                    </a>
                  ))}
                </div>
              ) : product.purchase_link ? (
                <a href={product.purchase_link} target="_blank" rel="noopener noreferrer" style={styles.purchaseBtn}>🛒 立即购买</a>
              ) : (
                <div>
                  <p style={styles.emptyText}>暂无上架信息，敬请期待。</p>
                  <a href="https://b23.tv/c9gCtts" target="_blank" rel="noopener noreferrer" style={styles.shopLink}>📺 前往 B 站店铺</a>
                </div>
              )}
            </div>

            <p style={styles.swipeHint}>← 向左滑动可返回列表</p>
          </div>
        </div>
      </div>
    </div>
  );
}

const styles = {
  container: {
    minHeight: '100vh',
    position: 'relative',
    backgroundColor: '#0f1115',
  },
  overlay: {
    position: 'fixed',
    top: 0, left: 0, right: 0, bottom: 0,
    backgroundColor: 'rgba(15, 17, 21, 0.75)',
    zIndex: 1,
  },
  contentLayer: {
    position: 'relative',
    zIndex: 2,
    padding: '16px 12px',
    paddingBottom: 'max(24px, env(safe-area-inset-bottom, 24px))',
    maxWidth: '600px',
    margin: '0 auto',
    overflowY: 'auto',
    WebkitOverflowScrolling: 'touch',
  },
  backButton: {
    background: 'rgba(255, 255, 255, 0.1)',
    border: '1px solid rgba(255, 255, 255, 0.2)',
    color: '#fff',
    padding: '10px 18px',
    borderRadius: '8px',
    cursor: 'pointer',
    fontSize: '14px',
    marginBottom: '16px',
  },
  pageContent: {
    backgroundColor: 'rgba(37, 40, 48, 0.95)',
    borderRadius: '16px',
    overflow: 'hidden',
    border: '1px solid rgba(255,255,255,0.08)',
  },
  imageSection: {
    position: 'relative',
    height: '280px',
    backgroundColor: '#000',
  },
  mainImage: {
    width: '100%',
    height: '100%',
    objectFit: 'contain',
  },
  noImage: {
    height: '100%',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    color: '#6b7280',
    fontSize: '16px',
  },
  carouselBtn: {
    position: 'absolute',
    top: '50%',
    left: 10,
    transform: 'translateY(-50%)',
    width: 40,
    height: 40,
    borderRadius: '50%',
    backgroundColor: 'rgba(0,0,0,0.5)',
    border: 'none',
    color: '#fff',
    fontSize: 24,
    cursor: 'pointer',
  },
  imageIndicators: {
    position: 'absolute',
    bottom: 12,
    left: '50%',
    transform: 'translateX(-50%)',
    display: 'flex',
    gap: 8,
  },
  indicator: {
    width: 8,
    height: 8,
    borderRadius: '50%',
    cursor: 'pointer',
  },
  infoSection: {
    padding: 16,
  },
  title: {
    fontSize: 22,
    fontWeight: 700,
    color: '#fff',
    margin: '0 0 4px 0',
  },
  subtitle: {
    fontSize: 14,
    color: '#9ca3af',
    margin: '0 0 16px 0',
  },
  sectionTitle: {
    fontSize: 14,
    fontWeight: 600,
    color: '#9ca3af',
    margin: '0 0 10px 0',
  },
  specsSection: { marginBottom: 16 },
  tableWrap: {
    overflowX: 'auto',
    borderRadius: 8,
    border: '1px solid rgba(255,255,255,0.1)',
  },
  table: { width: '100%', borderCollapse: 'collapse', fontSize: 13 },
  th: {
    padding: '8px 12px',
    textAlign: 'left',
    color: '#9ca3af',
    fontWeight: 600,
    borderBottom: '1px solid rgba(255,255,255,0.1)',
  },
  td: {
    padding: '8px 12px',
    color: '#d1d5db',
    borderBottom: '1px solid rgba(255,255,255,0.06)',
  },
  desc: { fontSize: 14, color: '#d1d5db', lineHeight: 1.6, marginBottom: 8 },
  descEn: { fontSize: 13, color: '#6b7280', lineHeight: 1.5, marginBottom: 16 },
  tags: { display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 16 },
  tag: {
    fontSize: 12,
    padding: '4px 10px',
    backgroundColor: 'rgba(59, 130, 246, 0.2)',
    color: '#60a5fa',
    borderRadius: 4,
  },
  purchaseSection: { marginBottom: 16 },
  purchaseGrid: { display: 'flex', flexWrap: 'wrap', gap: 10 },
  purchaseBtn: {
    display: 'inline-block',
    padding: '12px 24px',
    backgroundColor: '#22c55e',
    color: '#fff',
    borderRadius: '10px',
    textDecoration: 'none',
    fontSize: 15,
    fontWeight: 600,
  },
  shopLink: { fontSize: 14, color: '#60a5fa', textDecoration: 'none' },
  emptyText: { fontSize: 14, color: '#6b7280', margin: '0 0 10px 0', fontStyle: 'italic' },
  swipeHint: {
    fontSize: 12,
    color: 'rgba(255,255,255,0.4)',
    textAlign: 'center',
    margin: '16px 0 0 0',
  },
  loading: { textAlign: 'center', padding: 60, color: '#9ca3af' },
  spinner: {
    width: 40,
    height: 40,
    border: '3px solid rgba(255,255,255,0.1)',
    borderTopColor: '#3b82f6',
    borderRadius: '50%',
    animation: 'spin 1s linear infinite',
    margin: '0 auto 20px',
  },
  error: { textAlign: 'center', padding: 40, color: '#ef4444' },
};

export default ProductDetail;
